"use client"

import { useState } from "react"
import { ArrowLeft, Check, Copy, ExternalLink, FileText, LifeBuoy, Mail, Send } from "lucide-react"
import { smartCase } from "../utils/text"

const SUPPORT_EMAIL = "aplicacionesfcn@fondocafetero.com"

const SOFT_SHADOW = "shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)]"
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70"
const ICON_TILE = "flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#5D4037] text-white"
const PRIMARY_BUTTON = `inline-flex items-center justify-center gap-2 rounded-xl bg-[#5D4037] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#6D4C41] ${FOCUS}`
const SECONDARY_BUTTON = `inline-flex items-center justify-center gap-2 rounded-xl bg-cafe-100/50 px-5 py-2.5 text-sm font-medium text-cafe-700 transition-colors duration-200 hover:bg-cafe-100 ${FOCUS}`
const FIELD =
  "w-full rounded-xl border border-cafe-200/60 bg-white px-4 py-2.5 text-sm text-cafe-800 transition-colors duration-200 placeholder:text-neutral-300 hover:border-cafe-300 focus:border-cafe-400 focus:outline-none focus:ring-4 focus:ring-cafe-100/70"
const LABEL = "mb-2 block text-sm font-medium text-cafe-700"

// AdminTickets lee este asunto con /Soporte Técnico - (.*)/ y busca la aplicación por su nombre:
// el formato del asunto y del cuerpo no debe cambiar.
const buildSupportMessage = (form, user, applications) => {
  const selectedApp = applications.find((app) => app.id?.toString() === form.appId)
  const appName = selectedApp ? selectedApp.name || selectedApp.NombreAplicacion : ""

  const subject = `Soporte Técnico - ${appName}`
  const body = `
Nombre: ${form.name}
Email: ${form.email}
Usuario: ${user?.usuario || "No especificado"}
Empleado: ${user?.empleado?.NombreEmpleado || "No especificado"}
Dirección: ${user?.empleado?.Direccion || "No especificado"}
Puesto: ${user?.empleado?.Puesto || "No especificado"}
Aplicación: ${appName}
Prioridad: ${form.priority}

Descripción del problema:
${form.issue}
    `

  return { subject, body }
}

const buildMailto = ({ subject, body }) =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

export default function HelpSupportContent({ applications = [], user }) {
  const emptyForm = () => ({
    name: smartCase(user?.empleado?.NombreEmpleado || ""),
    email: "",
    appId: "",
    priority: "",
    issue: "",
  })

  // "overview" | "form" | "sent"
  const [step, setStep] = useState("overview")
  const [supportForm, setSupportForm] = useState(emptyForm)
  const [sentMessage, setSentMessage] = useState(null)
  const [copied, setCopied] = useState(null)

  const applicationsWithManual = applications.filter((app) => app.manual && app.manual.trim() !== "")

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
    } catch (error) {
      setCopied("error")
    }
    setTimeout(() => setCopied(null), 2500)
  }

  // Abre el programa de correo con la solicitud lista. El formulario no se borra hasta que el usuario lo decida.
  const handleSubmitSupportForm = (e) => {
    e.preventDefault()

    const message = buildSupportMessage(supportForm, user, applications)
    setSentMessage(message)
    setStep("sent")
    window.location.href = buildMailto(message)
  }

  const goToOverview = () => {
    setStep("overview")
    setSentMessage(null)
  }

  const startNewRequest = () => {
    setSupportForm(emptyForm())
    goToOverview()
  }

  const subtitle =
    step === "form"
      ? "Complete el formulario para solicitar asistencia."
      : step === "sent"
      ? "Su solicitud está lista para enviarse."
      : "Encuentre manuales y solicite asistencia técnica."

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Encabezado */}
      <div>
        <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-cafe-700 sm:text-4xl">
          Ayuda y soporte
        </h2>
        <p className="mt-1 text-sm text-neutral-400">{subtitle}</p>
      </div>

      {step === "overview" && (
        <>
          <section className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Solicitar soporte */}
              <div className={`flex flex-col rounded-xl bg-white p-6 ${SOFT_SHADOW}`}>
                <span className={ICON_TILE}>
                  <LifeBuoy className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-cafe-700">
                  Solicitar soporte técnico
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                  ¿Tiene problemas con alguna aplicación? Describa lo que ocurre y le prepararemos el correo para el
                  equipo de soporte.
                </p>
                <button onClick={() => setStep("form")} className={`mt-6 self-start ${PRIMARY_BUTTON}`}>
                  <Send className="h-4 w-4" />
                  Solicitar soporte
                </button>
              </div>

              {/* Contacto directo */}
              <div className={`flex flex-col rounded-xl bg-white p-6 ${SOFT_SHADOW}`}>
                <span className={ICON_TILE}>
                  <Mail className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-cafe-700">Contacto directo</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                  Para consultas que no requieran formulario, escriba directamente al equipo.
                </p>
                <div className="mt-6 flex items-center gap-2 rounded-xl bg-cafe-100/40 py-2 pl-4 pr-2">
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className={`min-w-0 flex-1 break-all text-sm font-medium text-cafe-800 hover:underline ${FOCUS} rounded`}
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  <button
                    onClick={() => copyText("email", SUPPORT_EMAIL)}
                    title={copied === "email" ? "Copiado" : "Copiar correo"}
                    aria-label="Copiar correo de soporte"
                    className={`flex-shrink-0 rounded-lg p-2 text-cafe-600 transition-colors hover:bg-white hover:text-cafe-800 ${FOCUS}`}
                  >
                    {copied === "email" ? <Check className="h-[18px] w-[18px]" /> : <Copy className="h-[18px] w-[18px]" />}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Manuales: solo aparece cuando hay alguno configurado */}
          {applicationsWithManual.length > 0 && (
            <section className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6">
              <h3 className="mb-5 font-display text-2xl font-semibold tracking-tight text-cafe-700">
                Manuales de usuario
              </h3>
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {applicationsWithManual.map((app) => (
                  <li key={app.id || app.Id}>
                    <a
                      href={app.manual}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group flex items-center gap-3 rounded-xl bg-white p-4 ${SOFT_SHADOW} transition-all duration-500 hover:-translate-y-0.5 ${FOCUS}`}
                    >
                      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#5D4037] text-white">
                        <FileText className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 block font-display text-[15px] font-semibold leading-snug text-cafe-700">
                          Manual de {smartCase(app.name || app.NombreAplicacion)}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-cafe-500">
                          {smartCase(app.unidadAdministrativa || "Sistema")}
                        </span>
                      </span>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 text-neutral-300 transition-colors group-hover:text-cafe-700" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {step === "form" && (
        <section className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6">
          <div className={`mx-auto max-w-3xl rounded-xl bg-white p-5 sm:p-8 ${SOFT_SHADOW}`}>
            <button
              type="button"
              onClick={goToOverview}
              className={`mb-6 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium text-cafe-600 transition-colors hover:text-cafe-800 ${FOCUS}`}
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </button>

            <form className="space-y-5" onSubmit={handleSubmitSupportForm}>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="support-name" className={LABEL}>
                    Nombre completo
                  </label>
                  <input
                    id="support-name"
                    type="text"
                    className={FIELD}
                    placeholder="Su nombre completo"
                    value={supportForm.name}
                    onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="support-email" className={LABEL}>
                    Correo electrónico
                  </label>
                  <input
                    id="support-email"
                    type="email"
                    className={FIELD}
                    placeholder="ejemplo@fondocafetero.com"
                    value={supportForm.email}
                    onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="support-app" className={LABEL}>
                    Aplicación
                  </label>
                  <select
                    id="support-app"
                    className={FIELD}
                    value={supportForm.appId}
                    onChange={(e) => setSupportForm({ ...supportForm, appId: e.target.value })}
                    required
                  >
                    <option value="">Seleccione una aplicación</option>
                    {applications.map((app) => (
                      <option key={app.id || app.Id} value={app.id || app.Id}>
                        {smartCase(app.name || app.NombreAplicacion)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="support-priority" className={LABEL}>
                    Prioridad
                  </label>
                  <select
                    id="support-priority"
                    className={FIELD}
                    value={supportForm.priority}
                    onChange={(e) => setSupportForm({ ...supportForm, priority: e.target.value })}
                    required
                  >
                    <option value="">Seleccione la prioridad</option>
                    <option value="baja">Baja: consulta general</option>
                    <option value="media">Media: problema funcional</option>
                    <option value="alta">Alta: error que afecta mi trabajo</option>
                    <option value="critica">Crítica: el sistema no funciona</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="support-issue" className={LABEL}>
                  Descripción del problema
                </label>
                <textarea
                  id="support-issue"
                  rows="5"
                  className={`${FIELD} resize-none`}
                  placeholder="Describa detalladamente el problema que está experimentando..."
                  value={supportForm.issue}
                  onChange={(e) => setSupportForm({ ...supportForm, issue: e.target.value })}
                  required
                ></textarea>
              </div>

              <p className="text-xs text-neutral-400">
                Se abrirá su aplicación de correo con la solicitud lista para enviar al equipo de soporte.
              </p>

              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={goToOverview} className={SECONDARY_BUTTON}>
                  Cancelar
                </button>
                <button type="submit" className={PRIMARY_BUTTON}>
                  <Send className="h-4 w-4" />
                  Enviar por correo
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {step === "sent" && sentMessage && (
        <section className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6">
          <div className={`mx-auto max-w-xl rounded-xl bg-white p-6 text-center sm:p-8 ${SOFT_SHADOW}`}>
            <span className={`${ICON_TILE} mx-auto`}>
              <Mail className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight text-cafe-700">Revise su correo</h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Se abrió su aplicación de correo con la solicitud lista. La solicitud no se envía hasta que usted pulse{" "}
              <span className="font-medium text-cafe-700">Enviar</span> allí. Si no se abrió, puede abrirlo de nuevo o
              copiar el mensaje y enviarlo a <span className="font-medium text-cafe-700">{SUPPORT_EMAIL}</span>.
            </p>

            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <a href={buildMailto(sentMessage)} className={PRIMARY_BUTTON}>
                <Mail className="h-4 w-4" />
                Abrir correo de nuevo
              </a>
              <button
                type="button"
                onClick={() =>
                  copyText("message", `Para: ${SUPPORT_EMAIL}\nAsunto: ${sentMessage.subject}\n${sentMessage.body}`)
                }
                className={SECONDARY_BUTTON}
              >
                {copied === "message" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied === "message" ? "Mensaje copiado" : copied === "error" ? "No se pudo copiar" : "Copiar mensaje"}
              </button>
            </div>

            <div className="mt-6 flex justify-center gap-4 text-sm">
              <button
                type="button"
                onClick={() => setStep("form")}
                className={`rounded font-medium text-cafe-600 hover:text-cafe-800 ${FOCUS}`}
              >
                Editar solicitud
              </button>
              <button
                type="button"
                onClick={startNewRequest}
                className={`rounded font-medium text-cafe-600 hover:text-cafe-800 ${FOCUS}`}
              >
                Terminar
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
